// Claiming a profile moved from a café's previous system (F-IAM-13; ADR-0048 §9, §13; spec/07 §3.1, spec/12 §3.2,
// spec/13 §8.2, spec/16 §4.3).
//
// A migration (F-HQ-40) leaves **unclaimed profiles** with money and time carried over for them. A person claims theirs
// by proving it is theirs, and only then does an account receive it: one ledger entry moves what the profile holds to
// the player's wallet (tax-paid money to tax-paid money) and its time to time passes on the classes it was valid on.
//
//   At a PC          GET  /v1/station/carried-over/options       is there anything to claim at this venue    station
//                    POST /v1/station/sign-in/carried-over/code  old username or email → a code to its email  station
//                    POST /v1/station/sign-in/carried-over       the code → signed in or a new account, claimed station
//   Automatically    GET  /v1/station/carried-over/offer         what waits for the signed-in player here      station
//                    POST /v1/station/carried-over/accept        "add it" — never without the player's yes     station
//   By the email     POST /v1/carried-over/link                  what the link in the moved email is for       public
//                    POST /v1/me/carried-over/claims             the signed-in account that proves its email   player
//   At the counter   POST /v1/organizations/{o}/imported-players/{ip}/claim/email-code   a code to a new email     wallet:topup
//                    POST /v1/organizations/{o}/imported-players/{ip}/claim              ID checked; link or create wallet:topup
//   The window's end GET  /v1/organizations/{o}/imported-players/write-offs              expiring and expired      credit:write_off
//                    POST /v1/organizations/{o}/imported-players/write-offs              the owner's write-off     credit:write_off
//   HQ               POST /v1/hq/organizations/{o}/migrations/{m}/moved-email            send, or send again       hq:organizations
//
// **Nothing here tells anyone whether an old account exists.** The kiosk's code request answers the same whether or not
// a profile matches, and whether or not it has an email; a wrong identifier and a wrong code are the same `otp-invalid`.
// **A minor's profile, and one the old system banned, is claimed only at the counter.** **Money moves only with the
// player's yes**: an offer is never accepted for them.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { ImportedPlayerIdSchema, MemberIdSchema, MigrationRunIdSchema, PlayerIdSchema, VenueIdSchema } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { CREDIT_WRITE_OFF, HQ_ORGANIZATIONS, WALLET_TOPUP } from "../permissions/index.js";
import { SignUpConsentSchema } from "../primitives/consent.js";
import { MoneySchema } from "../primitives/money.js";
import { OtpCodeSchema } from "../primitives/otp.js";
import { IsoDateTimeSchema, LocalDateSchema } from "../primitives/scalars.js";
import { OrganizationScopeParamsSchema } from "../primitives/scope.js";
import { UsernameSchema } from "../primitives/username.js";
import { ImportedPlayerSchema, MigrationRunSchema, MigrationSourcePlatformSchema } from "./migrations.js";
import { StaffPinSchema } from "./staff-pin.js";
import { StationEmailCodeSentSchema, StationProfileCompletionGrantSchema, StationSignedInPlayerSchema } from "./station-sign-in.js";
import { PERMISSION_EXTENSION } from "./audit.js";
const STATION_AUTHENTICATION = 'Station-authenticated: `Authorization: Bearer <kiosk token>` (spec/07 §4.1). A user session is not accepted here.';
const NEUTRAL = 'The answer is the same whether or not an old account matches, has an email, or may be claimed here: nothing says ' +
    'whether one exists.';
/** What a claim moved, or what waits to be: money (of it, tax-paid) and time, with the pass's last day. */
export const CarriedOverAmountsSchema = z
    .object({
    /** The profiles it came from, and the system they came from (absent when none). */
    profiles: z.int().min(0),
    sourcePlatform: MigrationSourcePlatformSchema.optional(),
    cash: MoneySchema,
    /** Of `cash`, money GST was already paid on at top-up in the old system. */
    cashTaxPaid: MoneySchema,
    timeSeconds: z.int().min(0),
    /** The last day the carried-over time can be played; null when none. */
    timeValidUntil: LocalDateSchema.nullable(),
})
    .strict()
    .openapi('CarriedOverAmounts');
// ---------------------------------------------------------------------------------------------
// At a PC (the kiosk)
// ---------------------------------------------------------------------------------------------
/** An old username or an old email address, as the player remembers it. */
const IdentifierSchema = z.string().trim().min(1).max(254);
export const StationCarriedOverOptionsSchema = z
    .object({
    /** Whether "Moving from our old system?" shows: this venue has profiles waiting that a PC can claim. */
    available: z.boolean(),
})
    .strict()
    .openapi('StationCarriedOverOptions');
export const StationCarriedOverCodeRequestSchema = z.object({ identifier: IdentifierSchema }).strict().openapi('StationCarriedOverCodeRequest');
export const StationCarriedOverClaimSchema = z
    .object({
    identifier: IdentifierSchema,
    code: OtpCodeSchema,
    /** Required when the code creates the account (the profile's email has none yet); without it the code is kept. */
    consent: SignUpConsentSchema.optional(),
    /** Optional, as for any kiosk sign-up: the old username is offered when it is free. */
    username: UsernameSchema.optional(),
})
    .strict()
    .openapi('StationCarriedOverClaim');
export const StationCarriedOverClaimResultSchema = z
    .object({
    player: StationSignedInPlayerSchema,
    createdAccount: z.boolean(),
    profileCompletion: StationProfileCompletionGrantSchema.optional(),
    /** What was added to the account. */
    carriedOver: CarriedOverAmountsSchema,
})
    .strict()
    .openapi('StationCarriedOverClaimResult');
export const StationCarriedOverOfferQuerySchema = z.object({ playerId: PlayerIdSchema }).openapi('StationCarriedOverOfferQuery');
export const StationCarriedOverOfferSchema = z
    .object({
    /** What waits for this player at this organisation under their verified email; null when nothing does. */
    offer: CarriedOverAmountsSchema.nullable(),
})
    .strict()
    .openapi('StationCarriedOverOffer');
export const StationCarriedOverAcceptSchema = z.object({ playerId: PlayerIdSchema }).strict().openapi('StationCarriedOverAccept');
export const StationCarriedOverAcceptedSchema = z.object({ carriedOver: CarriedOverAmountsSchema }).strict().openapi('StationCarriedOverAccepted');
const stationResponses = {
    400: commonProblemResponses[400],
    401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
    429: commonProblemResponses[429],
    500: commonProblemResponses[500],
};
export const getStationCarriedOverOptionsRoute = createRoute({
    method: 'get',
    path: '/v1/station/carried-over/options',
    operationId: 'getStationCarriedOverOptions',
    tags: ['identity', 'stations'],
    summary: 'Whether the lock screen offers "Moving from our old system?"',
    description: `${STATION_AUTHENTICATION} True while this venue has moved profiles waiting to be claimed. Names nobody.`,
    responses: { 200: jsonResponse(StationCarriedOverOptionsSchema, 'Whether to offer it.'), ...stationResponses },
});
export const requestStationCarriedOverCodeRoute = createRoute({
    method: 'post',
    path: '/v1/station/sign-in/carried-over/code',
    operationId: 'requestStationCarriedOverCode',
    tags: ['identity', 'stations'],
    summary: 'Send a code to the email of an old account, from a kiosk',
    description: `${STATION_AUTHENTICATION} The player types the username or email they used in the café’s previous system. When it ` +
        'names one waiting profile of this organisation that a PC may claim (an adult, not banned there, with an email), a ' +
        '6-digit code valid for 10 minutes goes to that profile’s email. ' +
        `${NEUTRAL} Counted per station, and per identifier: 3 per 10 minutes, 10 a day.`,
    request: { body: { content: { 'application/json': { schema: StationCarriedOverCodeRequestSchema } }, required: true } },
    responses: { 202: jsonResponse(StationEmailCodeSentSchema, 'If an old account matched, a code is on its way to its email.'), ...stationResponses },
});
export const claimStationCarriedOverRoute = createRoute({
    method: 'post',
    path: '/v1/station/sign-in/carried-over',
    operationId: 'claimStationCarriedOver',
    tags: ['identity', 'stations'],
    summary: 'Claim an old account at a kiosk with the emailed code',
    description: `${STATION_AUTHENTICATION} Verifies the code sent to the old account’s email, signs in the Clavian account with that ` +
        'email or creates it (only with `consent`: without it the answer is `consent-required` and the code is kept), and ' +
        'moves everything waiting under that email at this organisation to it, in one ledger entry per profile. A wrong ' +
        'identifier and a wrong code are the same `otp-invalid`.',
    request: { body: { content: { 'application/json': { schema: StationCarriedOverClaimSchema } }, required: true } },
    responses: {
        200: jsonResponse(StationCarriedOverClaimResultSchema, 'Signed in, with what was added.'),
        400: problemResponse('The code is wrong, expired or used up, or a new account was asked for without consent.', 'validation-failed', 'otp-invalid', 'otp-expired', 'consent-required'),
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        403: problemResponse('The player is banned here. Or: this venue does not create accounts at its PCs.', 'player-banned', 'sign-up-disabled'),
        409: problemResponse('The terms or privacy version shown is not the one in force.', 'consent-version-outdated'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getStationCarriedOverOfferRoute = createRoute({
    method: 'get',
    path: '/v1/station/carried-over/offer',
    operationId: 'getStationCarriedOverOffer',
    tags: ['identity', 'stations'],
    summary: 'What waits for the signed-in player from the café’s previous system',
    description: `${STATION_AUTHENTICATION} After a sign-in: the profiles of this organisation whose email is exactly the player’s ` +
        'verified email, that a PC may claim, totalled. The kiosk asks "add it?" — nothing moves until the player says yes.',
    request: { query: StationCarriedOverOfferQuerySchema },
    responses: { 200: jsonResponse(StationCarriedOverOfferSchema, 'The offer, or null.'), ...stationResponses },
});
export const acceptStationCarriedOverOfferRoute = createRoute({
    method: 'post',
    path: '/v1/station/carried-over/accept',
    operationId: 'acceptStationCarriedOverOffer',
    tags: ['identity', 'stations'],
    summary: 'The player said yes: add what waits for them',
    description: `${STATION_AUTHENTICATION} Claims every profile the offer named, in one ledger entry each. Nothing waiting is \`not-found\`.`,
    request: { body: { content: { 'application/json': { schema: StationCarriedOverAcceptSchema } }, required: true } },
    responses: {
        200: jsonResponse(StationCarriedOverAcceptedSchema, 'What was added.'),
        ...stationResponses,
        404: problemResponse('Nothing waits for this player here (already claimed, or never matched).', 'not-found'),
    },
});
// ---------------------------------------------------------------------------------------------
// By the link in the "account moved" email (player web)
// ---------------------------------------------------------------------------------------------
/** The signed token in the moved email's link: opaque, never logged, sent only in a request body. */
const LinkTokenSchema = z.string().min(32).max(512);
export const CarriedOverLinkRequestSchema = z.object({ token: LinkTokenSchema }).strict().openapi('CarriedOverLinkRequest');
export const CARRIED_OVER_LINK_STATES = ['waiting', 'claimed', 'closed'];
export const CarriedOverLinkSchema = z
    .object({
    /** `waiting` to be claimed; `claimed` already; `closed`: the window ended, it was undone, or it is the counter’s. */
    state: z.enum(CARRIED_OVER_LINK_STATES),
    venueName: z.string(),
    /** The email the link went to, masked: the account that claims must have exactly this one, verified. */
    email: z.string(),
    /** Only to claim at the front desk (a minor’s profile, or one the old system banned). */
    counterOnly: z.boolean(),
    carriedOver: CarriedOverAmountsSchema,
    claimWindowEndsOn: LocalDateSchema,
})
    .strict()
    .openapi('CarriedOverLink');
export const readCarriedOverLinkRoute = createRoute({
    method: 'post',
    path: '/v1/carried-over/link',
    operationId: 'readCarriedOverLink',
    tags: ['identity'],
    summary: 'What a moved email’s link is for',
    description: 'Public: the token is the proof. It names one profile, the email it was sent to and how long it waits. The token is ' +
        'sent in the body, never in a URL the server sees. Rate-limited per caller.',
    request: { body: { content: { 'application/json': { schema: CarriedOverLinkRequestSchema } }, required: true } },
    responses: {
        200: jsonResponse(CarriedOverLinkSchema, 'What waits, and where.'),
        400: problemResponse('The link is not one we sent, or it has run out.', 'validation-failed', 'carried-over-link-invalid'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const ClaimCarriedOverByLinkSchema = z.object({ token: LinkTokenSchema }).strict().openapi('ClaimCarriedOverByLink');
export const CarriedOverClaimedSchema = z.object({ carriedOver: CarriedOverAmountsSchema }).strict().openapi('CarriedOverClaimed');
export const claimCarriedOverByLinkRoute = createRoute({
    method: 'post',
    path: '/v1/me/carried-over/claims',
    operationId: 'claimCarriedOverByLink',
    tags: ['me'],
    summary: 'Claim what the moved email’s link names, into your own account',
    description: 'Needs a signed-in player whose verified email is exactly the one the link went to. Moves every profile waiting under ' +
        'that email at that organisation to the account, one ledger entry each, and makes the player one of its players.',
    request: { body: { content: { 'application/json': { schema: ClaimCarriedOverByLinkSchema } }, required: true } },
    responses: {
        200: jsonResponse(CarriedOverClaimedSchema, 'What was added.'),
        400: problemResponse('The link is not one we sent, or it has run out.', 'validation-failed', 'carried-over-link-invalid'),
        401: commonProblemResponses[401],
        409: problemResponse('This account’s verified email is not the one the link went to; or the profile is the front desk’s; or nothing waits.', 'carried-over-email-mismatch', 'carried-over-counter-only', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// At the counter (the venue console)
// ---------------------------------------------------------------------------------------------
const ImportedPlayerParamsSchema = z
    .object({ organizationId: OrganizationScopeParamsSchema.shape.organizationId, importedPlayerId: ImportedPlayerIdSchema })
    .openapi('ImportedPlayerParams');
const EmailSchema = z.string().trim().min(3).max(254).regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'An email address');
export const CounterClaimEmailCodeSchema = z.object({ email: EmailSchema }).strict().openapi('CounterClaimEmailCode');
export const CounterClaimSchema = z
    .object({
    /** An account the organisation already knows: staff found the player and checked it is them. */
    playerId: PlayerIdSchema.optional(),
    /**
     * Or an email the player proves at the counter with the code sent to it (`code`): the account with it, or a new one —
     * created only with the player's own consent (`consent`), which the dialog asks them for.
     */
    email: EmailSchema.optional(),
    code: OtpCodeSchema.optional(),
    consent: SignUpConsentSchema.optional(),
    /** Staff checked the person is who the profile says (old username, phone, photo ID as the venue requires). */
    identityChecked: z.boolean(),
    /** For a minor's profile: the parent or guardian present, by name. Required then, refused otherwise. */
    guardian: z.object({ present: z.boolean(), name: z.string().trim().min(2).max(120) }).strict().openapi('CounterClaimGuardian').optional(),
    /** Above `migration.counterClaimApprovalAmount`: a manager's (`wallet:adjust`) staff PIN, not the requester's own. */
    approval: z.object({ memberId: MemberIdSchema, pin: StaffPinSchema }).strict().openapi('CounterClaimApproval').optional(),
})
    .strict()
    .refine((body) => (body.playerId === undefined) !== (body.email === undefined && body.code === undefined), {
    message: 'Name the player (`playerId`), or the email and its code (`email`, `code`): one of the two.',
})
    .refine((body) => body.email === undefined || body.code !== undefined, { message: 'An email needs the code sent to it (`code`).' })
    .refine((body) => body.identityChecked && (body.guardian === undefined || body.guardian.present), {
    message: 'Staff check the person’s identity (`identityChecked: true`), and a guardian recorded is present.',
})
    .openapi('CounterClaim');
export const CounterClaimResultSchema = z
    .object({
    importedPlayer: ImportedPlayerSchema,
    player: StationSignedInPlayerSchema,
    createdAccount: z.boolean(),
    carriedOver: CarriedOverAmountsSchema,
})
    .strict()
    .openapi('CounterClaimResult');
/** Who may approve a counter claim above the threshold here: a manager with a staff PIN at the profile's venue. */
export const CounterClaimApproverSchema = z
    .object({ memberId: MemberIdSchema, name: z.string() })
    .strict()
    .openapi('CounterClaimApprover');
export const CounterClaimApproverListSchema = z
    .object({ data: z.array(CounterClaimApproverSchema) })
    .strict()
    .openapi('CounterClaimApproverList');
export const listCounterClaimApproversRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/imported-players/{importedPlayerId}/claim/approvers',
    operationId: 'listCounterClaimApprovers',
    tags: ['players'],
    summary: 'Who can approve this counter claim with their staff PIN',
    description: 'The members who hold `wallet:adjust` and have a staff PIN at the profile’s venue, other than the caller, by name: what ' +
        'the claim dialog lists when the money is above `migration.counterClaimApprovalAmount`. Requires `wallet:topup`.',
    [PERMISSION_EXTENSION]: WALLET_TOPUP,
    request: { params: ImportedPlayerParamsSchema },
    responses: {
        200: jsonResponse(CounterClaimApproverListSchema, 'The approvers.'),
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such moved player here.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const requestCounterClaimEmailCodeRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/imported-players/{importedPlayerId}/claim/email-code',
    operationId: 'requestCounterClaimEmailCode',
    tags: ['players'],
    summary: 'Send a code to the email a moved player is claiming with, at the counter',
    description: 'A 6-digit code valid for 10 minutes to the address the player gives — for a player with no email on file, or one who ' +
        'wants another. The player reads it from their own phone; it proves the address. 3 per address per 10 minutes, 10 a day. ' +
        'Requires `wallet:topup`.',
    [PERMISSION_EXTENSION]: WALLET_TOPUP,
    request: { params: ImportedPlayerParamsSchema, body: { content: { 'application/json': { schema: CounterClaimEmailCodeSchema } }, required: true } },
    responses: {
        202: jsonResponse(StationEmailCodeSentSchema, 'The code is on its way.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such moved player here.', 'not-found'),
        409: problemResponse('The profile is no longer waiting to be claimed.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
        503: problemResponse('No email provider is configured; nothing was sent.', 'otp-delivery-unavailable'),
    },
});
export const claimAtCounterRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/imported-players/{importedPlayerId}/claim',
    operationId: 'claimAtCounter',
    tags: ['players'],
    summary: 'Claim a moved profile at the counter',
    description: 'Staff checked the person’s identity (`players:id_check` too). The money and time move to an existing player of the ' +
        'organisation, or to the account of an email the player proves with a code (created with their consent when new). A ' +
        'minor’s profile needs a parent or guardian present, by name. Above `migration.counterClaimApprovalAmount` (₹2,000; cash ' +
        'only, never time) a manager’s staff PIN approves it: `carried-over-approval-required` asks for one. One ledger entry; ' +
        'audited with who checked and who approved. Requires `wallet:topup`.',
    [PERMISSION_EXTENSION]: WALLET_TOPUP,
    request: { params: ImportedPlayerParamsSchema, body: { content: { 'application/json': { schema: CounterClaimSchema } }, required: true } },
    responses: {
        200: jsonResponse(CounterClaimResultSchema, 'Claimed.'),
        400: problemResponse('The code is wrong or expired; the guardian is missing or not needed; consent is missing for a new account; or the manager’s PIN is wrong.', 'validation-failed', 'otp-invalid', 'otp-expired', 'consent-required', 'staff-pin-incorrect'),
        401: commonProblemResponses[401],
        403: problemResponse('Not allowed; or above the threshold without a manager’s PIN, or with the requester’s own; or the PIN is locked.', 'forbidden', 'carried-over-approval-required', 'staff-pin-locked', 'player-banned'),
        404: problemResponse('No such moved player, or no such player here.', 'not-found'),
        409: problemResponse('The profile is no longer waiting to be claimed; or the terms version is outdated.', 'conflict', 'consent-version-outdated'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// The end of the claim window: the owner's write-off
// ---------------------------------------------------------------------------------------------
export const CarriedOverWriteOffItemSchema = z
    .object({
    id: ImportedPlayerIdSchema,
    venueId: VenueIdSchema,
    sourcePlatform: MigrationSourcePlatformSchema,
    /** The old username — `null` once the window has ended and the personal details are erased. */
    username: z.string().nullable(),
    /** `expiring`: the window ends within 30 days; `ended`: it has ended, the details are erased, the money waits. */
    state: z.enum(['expiring', 'ended']),
    claimWindowEndsOn: LocalDateSchema,
    cash: MoneySchema,
    cashTaxPaid: MoneySchema,
    timeSeconds: z.int().min(0),
})
    .strict()
    .openapi('CarriedOverWriteOffItem');
export const CarriedOverWriteOffListSchema = z
    .object({
    data: z.array(CarriedOverWriteOffItemSchema),
    /** What the ended profiles hold, all together: what a write-off of them all would close. */
    endedTotal: MoneySchema,
    expiringTotal: MoneySchema,
})
    .strict()
    .openapi('CarriedOverWriteOffList');
export const CarriedOverWriteOffSchema = z
    .object({
    /** Profiles whose window has ended; each is written off whole. */
    importedPlayerIds: z.array(ImportedPlayerIdSchema).min(1).max(200),
    /** The owner's own confirmation that the total is right: must be true. */
    confirm: z.boolean(),
    reason: z.string().trim().min(3).max(500).optional(),
})
    .strict()
    .refine((body) => body.confirm, { message: 'The owner confirms the total (`confirm: true`).' })
    .openapi('CarriedOverWriteOff');
export const CarriedOverWrittenOffSchema = z
    .object({ writtenOff: z.int().min(0), amount: MoneySchema, at: IsoDateTimeSchema })
    .strict()
    .openapi('CarriedOverWrittenOff');
export const listCarriedOverWriteOffsRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/imported-players/write-offs',
    operationId: 'listCarriedOverWriteOffs',
    tags: ['players'],
    summary: 'Moved balances whose claim window ends soon, or has ended',
    description: 'Profiles still waiting whose window ends within 30 days, and those whose window has ended — details erased, money ' +
        'still owed until the owner writes it off — with what each holds. Requires `credit:write_off` (the owner’s by default).',
    [PERMISSION_EXTENSION]: CREDIT_WRITE_OFF,
    request: { params: OrganizationScopeParamsSchema },
    responses: {
        200: jsonResponse(CarriedOverWriteOffListSchema, 'The list.'),
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const writeOffCarriedOverRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/imported-players/write-offs',
    operationId: 'writeOffCarriedOver',
    tags: ['players'],
    summary: 'Write off the money of profiles whose claim window has ended',
    description: 'The owner’s sign-off: each profile’s money moves from what is owed to the organisation’s forfeited balance (other ' +
        'income, never takings or revenue), one ledger entry each, one audit row each. Only for ended windows. Requires ' +
        '`credit:write_off` (the owner’s by default).',
    [PERMISSION_EXTENSION]: CREDIT_WRITE_OFF,
    request: { params: OrganizationScopeParamsSchema, body: { content: { 'application/json': { schema: CarriedOverWriteOffSchema } }, required: true } },
    responses: {
        200: jsonResponse(CarriedOverWrittenOffSchema, 'Written off.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such profile here.', 'not-found'),
        409: problemResponse('A profile’s window has not ended, it was claimed, or it is already written off.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// HQ: the "account moved" email
// ---------------------------------------------------------------------------------------------
export const SendMovedEmailSchema = z
    .object({
    /** `true` starts another round to everyone still waiting; `false` carries on (or starts) the current one. */
    resend: z.boolean(),
})
    .strict()
    .openapi('SendMovedEmail');
const RunParamsSchema = z
    .object({ organizationId: OrganizationScopeParamsSchema.shape.organizationId, migrationId: MigrationRunIdSchema })
    .openapi('MovedEmailRunParams');
export const sendMovedEmailRoute = createRoute({
    method: 'post',
    path: '/v1/hq/organizations/{organizationId}/migrations/{migrationId}/moved-email',
    operationId: 'sendMovedEmail',
    tags: ['hq'],
    summary: 'Send the "account moved" email to the run’s waiting players',
    description: 'Under the café’s name: what was moved, how to claim it (a link, valid until the window ends), and how to ask for the ' +
        'details to be deleted — the notice of what is held. Sent in batches: each call sends the next batch and returns the ' +
        'run with `movedEmail.pending`; call again until it is 0. Never to a profile already claimed or with no email. Audited. ' +
        'Platform scope: `hq:organizations` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_ORGANIZATIONS,
    request: { params: RunParamsSchema, body: { content: { 'application/json': { schema: SendMovedEmailSchema } }, required: true } },
    responses: {
        200: jsonResponse(MigrationRunSchema, 'The run, with where its email stands.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('Not HQ, no `hq:organizations`, or no second factor on the session.', 'forbidden', 'second-factor-required'),
        404: problemResponse('No such organisation or migration.', 'not-found'),
        409: problemResponse('The import has not completed.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
        503: problemResponse('No email provider is configured; nothing was sent.', 'otp-delivery-unavailable'),
    },
});
/** Every claiming route, for the contract index. */
export const MIGRATION_CLAIM_ROUTES = [
    getStationCarriedOverOptionsRoute,
    requestStationCarriedOverCodeRoute,
    claimStationCarriedOverRoute,
    getStationCarriedOverOfferRoute,
    acceptStationCarriedOverOfferRoute,
    readCarriedOverLinkRoute,
    claimCarriedOverByLinkRoute,
    requestCounterClaimEmailCodeRoute,
    listCounterClaimApproversRoute,
    claimAtCounterRoute,
    listCarriedOverWriteOffsRoute,
    writeOffCarriedOverRoute,
    sendMovedEmailRoute,
];
//# sourceMappingURL=migration-claims.js.map